import React, { useEffect, useState } from 'react';
import { FileText, Download, Eye, Plus, Edit, Trash2, X, Save, Upload } from 'lucide-react';
import config from '../utils/config';
import Swal from 'sweetalert2';

const API_URL = config.url;

const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename || 'press.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (e) {
    console.error('Download error:', e);
    Swal.fire({
      icon: 'error',
      title: 'Download failed',
      text: 'Please try again.',
      confirmButtonColor: '#f97316'
    });
  }
};

const PressAdmin = () => {
  const [pressList, setPressList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pdf: null
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPress();
  }, []);

  const fetchPress = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/press`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setPressList(data);
    } catch (err) {
      setError('Failed to load press releases');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setFormData(prev => ({ ...prev, pdf: file }));
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid file',
        text: 'Please select a valid PDF file',
        confirmButtonColor: '#f97316'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Title is required',
        confirmButtonColor: '#f97316'
      });
      return;
    }
    if (!editingItem && !formData.pdf) {
      Swal.fire({
        icon: 'warning',
        title: 'PDF required',
        text: 'PDF file is required for new press releases',
        confirmButtonColor: '#f97316'
      });
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('description', formData.description.trim());
      if (formData.pdf) {
        formDataToSend.append('pdf', formData.pdf);
      }

      const url = editingItem 
        ? `${API_URL}/press/${editingItem.id}`
        : `${API_URL}/press`;
      
      const method = editingItem ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (!res.ok) throw new Error('Failed to save');

      await fetchPress();
      handleCloseModal();
      Swal.fire({
        icon: 'success',
        title: editingItem ? 'Press release updated!' : 'Press release added!',
        timer: 1600,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Save error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Failed to save press release',
        text: 'Please try again.',
        confirmButtonColor: '#f97316'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this press release?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/press/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Failed to delete');

      await fetchPress();
      Swal.fire({
        icon: 'success',
        title: 'Press release deleted',
        timer: 1400,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Delete error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Failed to delete press release',
        text: 'Please try again.',
        confirmButtonColor: '#f97316'
      });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      pdf: null
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ title: '', description: '', pdf: null });
    setError('');
  };

  return (
    <section className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold flex items-center gap-3 text-gray-800">
              <FileText className="w-8 h-8 text-orange-500" /> 
              Press Releases Management
            </h2>
            <p className="text-gray-600 mt-2">Manage press releases and media coverage</p>
          </div>
          <button
            className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg transition-all duration-200 hover:scale-105"
            onClick={() => setShowModal(true)}
          >
            <Plus className="w-5 h-5" /> Add Press Release
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <p className="text-red-600 font-medium">{error}</p>
            <button 
              onClick={fetchPress}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Press List */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pressList.length === 0 ? (
              <div className="col-span-full text-center py-20">
                <div className="bg-white rounded-3xl p-12 shadow-xl max-w-md mx-auto">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    No Press Releases
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Start by adding your first press release.
                  </p>
                  <button
                    onClick={() => setShowModal(true)}
                    className="bg-orange-600 text-white px-6 py-3 rounded-xl hover:bg-orange-700 transition-colors"
                  >
                    Add First Press Release
                  </button>
                </div>
              </div>
            ) : (
              pressList.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100"
                >
                  {/* PDF Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-28 rounded-2xl bg-gradient-to-br from-orange-100 to-pink-100 flex items-center justify-center shadow-lg">
                      <FileText className="w-10 h-10 text-orange-500" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-800 line-clamp-2">
                      {item.title}
                    </h3>
                    
                    {item.description && (
                      <p className="text-gray-600 text-sm line-clamp-3">
                        {item.description}
                      </p>
                    )}

                    <div className="text-xs text-gray-500">
                      Created: {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-4">
                      <a
                        href={getFullUrl(item.pdf_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-1 bg-blue-50 text-blue-700 hover:bg-blue-100 py-2 px-3 rounded-lg text-sm font-medium transition-colors"
                        title="View PDF"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </a>
                      <button
                        onClick={() => downloadFile(
                          getFullUrl(item.pdf_url),
                          item.title ? item.title.replace(/[^a-zA-Z0-9-_]/g, '_') + '.pdf' : 'press.pdf'
                        )}
                        className="flex-1 flex items-center justify-center gap-1 bg-gray-50 text-gray-700 hover:bg-gray-100 py-2 px-3 rounded-lg text-sm font-medium transition-colors"
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>

                    {/* Admin Actions */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="flex-1 flex items-center justify-center gap-1 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 py-2 px-3 rounded-lg text-sm font-medium transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="flex-1 flex items-center justify-center gap-1 bg-red-50 text-red-700 hover:bg-red-100 py-2 px-3 rounded-lg text-sm font-medium transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">
                  {editingItem ? 'Edit Press Release' : 'Add Press Release'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                    placeholder="Enter press release title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 resize-none"
                    rows="4"
                    placeholder="Enter press release description (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PDF File {!editingItem && '*'}
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-orange-400 transition-colors">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      id="pdf-upload"
                      required={!editingItem}
                    />
                    <label htmlFor="pdf-upload" className="cursor-pointer">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">
                        {formData.pdf ? formData.pdf.name : 'Click to upload PDF file'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {editingItem ? 'Leave empty to keep current file' : 'PDF files only'}
                      </p>
                    </label>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        {editingItem ? 'Update' : 'Add'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PressAdmin;